(() => {
  const ROOT = '.product__description';
  const BEFORE = 'custom-description-gap-before-v1';
  const AFTER = 'custom-description-gap-after-v1';
  const EMPTY = 'custom-description-gap-empty-v1';
  const INLINE = 'custom-description-inline-gap-v1';
  const COLOR_MEDIA = 'custom-description-color-media-gap-v1';
  const initialized = new WeakSet();
  const compact = (node) => (node.textContent || '').replace(/\s+/g, ' ').trim();
  const feature = (value) => /^(?:[•●▪]\s*|-\s+|(?:Fabric|Material|Length|Neckline|Sleeve|Back|Embellishment|Silhouette|Size|Color|Dimensions|The ribbon color|The pearl section)\s*:|Tiara (?:diameter|size)|(?:Veil |Total (?:sash )?)?Length\s*[:\d]|Height\s*[:\d]|One size fits|4-in-1 Relaxed fit|Size-{2}|Embellishment(?: size)?(?:\s*[:-]|$))/i.test(value);
  const moreColors = (value) => /^more\s+colou?rs?\s+avail(?:a|a?i?)ble\s*[:!]?$/i.test(value);
  const media = 'img, picture, video, iframe, svg, table, details, input, button';
  const blank = (node) => node.nodeType === Node.TEXT_NODE
    ? !compact(node)
    : node.nodeType !== Node.ELEMENT_NODE || (!compact(node) && !node.matches(media) && !node.querySelector(media));

  function hideBlank(node) {
    if (node.nodeType === Node.ELEMENT_NODE) node.classList.add(EMPTY);
  }

  // A boundary is whitespace only. Keep every original content node and attribute.
  function trimTrailingSpace(element) {
    if (element.matches('img, picture, video, iframe, svg, table, details')) return;
    const children = [...element.childNodes];
    for (let i = children.length - 1; i >= 0; i -= 1) {
      const child = children[i];
      if (blank(child)) {
        hideBlank(child);
        continue;
      }
      if (child.nodeType === Node.ELEMENT_NODE && !child.matches(media)) {
        child.classList.add(AFTER);
        trimTrailingSpace(child);
      }
      break;
    }
  }

  function boundaryBefore(target, root, colorMedia = false) {
    let right = target;
    while (right && right !== root) {
      let left = right.previousSibling;
      while (left && blank(left)) {
        hideBlank(left);
        left = left.previousSibling;
      }
      if (left) {
        if (left.nodeType !== Node.ELEMENT_NODE) return;
        if (!left.matches(media)) {
          left.classList.add(AFTER);
          trimTrailingSpace(left);
        }
        right.classList.add(BEFORE);
        if (colorMedia) right.classList.add(COLOR_MEDIA);
        return;
      }
      right = right.parentElement;
    }
  }

  function inlineFeatureGap(element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
    const lines = [{ text: '', breaks: [] }];
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR') {
        lines.push({ text: '', breaks: [...lines.at(-1).breaks, node] });
      } else if (node.nodeType === Node.TEXT_NODE && compact(node)) {
        const line = lines.at(-1);
        line.text += node.textContent;
      }
    }
    const index = lines.findIndex((line) => feature(line.text.trim()));
    if (index < 1 || !lines.slice(0, index).some((line) => line.text.trim())) return;
    let start = index - 1;
    while (start >= 0 && !lines[start].text.trim()) start -= 1;
    const breaks = lines[index].breaks.slice(lines[start].breaks.length);
    if (!breaks.length) return;
    breaks.forEach(hideBlank);
    const spacer = document.createElement('span');
    spacer.className = INLINE;
    spacer.setAttribute('aria-hidden', 'true');
    breaks.at(-1).after(spacer);
  }

  function nextContentAfter(element, root) {
    let current = element;
    while (current && current !== root) {
      let next = current.nextElementSibling;
      while (next && blank(next)) next = next.nextElementSibling;
      if (next) return next;
      current = current.parentElement;
    }
    return null;
  }

  function inlineColorGaps(element, root) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
    const tokens = [];
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if ((node.nodeType === Node.TEXT_NODE && compact(node))
        || (node.nodeType === Node.ELEMENT_NODE && node.matches('br, img, picture'))) tokens.push(node);
    }
    const index = tokens.findIndex((node) => node.nodeType === Node.TEXT_NODE && moreColors(compact(node)));
    if (index < 0) return;
    const heading = tokens[index];
    let before = index - 1;
    while (before >= 0 && tokens[before].nodeType === Node.ELEMENT_NODE && tokens[before].tagName === 'BR') before -= 1;
    if (before >= 0) {
      tokens.slice(before + 1, index).forEach(hideBlank);
      const spacer = document.createElement('span');
      spacer.className = INLINE;
      spacer.setAttribute('aria-hidden', 'true');
      heading.before(spacer);
    } else {
      boundaryBefore(element, root);
    }
    let after = index + 1;
    while (after < tokens.length && tokens[after].nodeType === Node.ELEMENT_NODE && tokens[after].tagName === 'BR') after += 1;
    if (after < tokens.length) {
      tokens.slice(index + 1, after).forEach(hideBlank);
      const spacer = document.createElement('span');
      spacer.className = INLINE;
      spacer.setAttribute('aria-hidden', 'true');
      spacer.classList.add(COLOR_MEDIA);
      heading.after(spacer);
    } else {
      const next = nextContentAfter(element, root);
      if (next && (next.matches('img, picture') || next.querySelector('img, picture'))) boundaryBefore(next, root, true);
    }
  }

  function apply(root) {
    if (initialized.has(root)) return;
    initialized.add(root);
    const prose = [...root.querySelectorAll('p, div, ul, ol')].filter((element) =>
      !element.closest('details, table, [data-custom-more-colors-flow-boundary]')
      && !element.querySelector('p, div, ul, ol, table, details, video, iframe')
    );
    for (const element of prose) {
      const value = compact(element);
      if (feature(value) || element.matches('ul, ol')) {
        let previous = element.previousElementSibling;
        while (previous && blank(previous)) previous = previous.previousElementSibling;
        if (previous && !feature(compact(previous)) && !moreColors(compact(previous))) {
          boundaryBefore(element, root);
        }
      } else if (!moreColors(value)) {
        inlineFeatureGap(element);
      }
      if (!moreColors(value) || element.querySelector('img')) inlineColorGaps(element, root);
    }
    const headings = [...root.querySelectorAll('p, div, h2, h3, h4')].filter((element) =>
      !element.closest('details, table') && moreColors(compact(element))
      && !element.querySelector('p, div, h2, h3, h4, img')
    );
    for (const heading of headings) {
      boundaryBefore(heading, root);
      const next = nextContentAfter(heading, root);
      if (next && (next.matches('img, picture') || next.querySelector('img, picture'))) {
        boundaryBefore(next, root, true);
      }
    }
    // Older listings may keep the heading directly beside paragraph wrappers.
    root.querySelectorAll('b, strong, span').forEach((heading) => {
      if (heading.closest('p, h2, h3, h4, details, table') || !moreColors(compact(heading))) return;
      if (heading.parentElement && moreColors(compact(heading.parentElement))) return;
      const next = nextContentAfter(heading, root);
      if (next && (next.matches('img, picture') || next.querySelector('img, picture'))) {
        boundaryBefore(next, root, true);
      }
    });
    root.querySelectorAll('details').forEach((chart) => {
      const summary = chart.querySelector('summary');
      if (summary && /sizechart/i.test(compact(summary).replace(/\s+/g, '')) && chart.querySelector('table')) {
        boundaryBefore(chart, root);
      }
    });
  }

  const refresh = () => document.querySelectorAll(ROOT).forEach(apply);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('american-stride:description-spacing', refresh);
})();
